import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Typography,
  Box,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Tooltip,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import { CrearItemServicio } from "../../utils/CrearItemServicio";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const QUOTA_OPTIONS = [
  { label: 3, key: "cuota_1" },
  { label: 6, key: "cuota_2" },
  { label: 9, key: "cuota_3" },
  { label: 12, key: "cuota_4" },
  { label: 18, key: "cuota_5" },
];

const NichosAtaud = ({ agregarAlPresupuesto, resetTrigger, deshabilitarBoton }) => {
  // Lista de planes de Parcela

  // Plan seleccionado y código de parcela
  const [planes, setPlanes] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [modalidad_pago, setmodalidad_pago] = useState("");
  const [cuotas, setCuotas] = useState(3);
  const [monto_por_cuotas, setmonto_por_cuotas] = useState(0);
  const [precio_plan, setprecio_plan] = useState(0);
  const [metodo_pago, setmetodo_pago] = useState("");
  const [contadoChecked, setContadoChecked] = useState(true);

  const auth = getAuthData();
  const token = auth?.token;

  const precioContado_bcv = planSeleccionado
    ? parseFloat(planSeleccionado.de_contado_bcv) || 0
    : 0;
  const precioContado = planSeleccionado
    ? parseFloat(planSeleccionado.de_contado) || 0
    : 0;

  // Calcular cuotas disponibles
  const availableQuotas = React.useMemo(() => {
    if (!planSeleccionado) return [];
    return QUOTA_OPTIONS.filter((option) => {
      const val = planSeleccionado[option.key];
      return val !== null && val !== undefined && parseFloat(val) > 0;
    });
  }, [planSeleccionado]);

  // Effect to ensure valid quota selection
  useEffect(() => {
    if (modalidad_pago === "financiado" && availableQuotas.length > 0) {
      const isCurrentValid = availableQuotas.some((q) => q.label === cuotas);
      if (!isCurrentValid) {
        setCuotas(availableQuotas[0].label);
      }
    }
  }, [modalidad_pago, availableQuotas, cuotas]);

  // ===== Manejo de selección de plan y código =====

  useEffect(() => {
    const fetchPlanes = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/planes/productos?tipo_servicio=nicho-ataud`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPlanes(response.data);
      } catch (error) {
        console.error("Error al obtener los planes:", error);
        toast.error("Error al cargar los planes de nichos de Ataúd.");
      }
    };
    fetchPlanes();
  }, [token]);

  useEffect(() => {
    setPlanSeleccionado(null);
    setmodalidad_pago("");
    setCuotas(3);
    setmonto_por_cuotas(0);
    setmetodo_pago("");
    setprecio_plan(0);
    setCodigo("");
    setContadoChecked(true);
  }, [resetTrigger]);

  const handlemodalidad_pagoChange = (e) => {
    setmodalidad_pago(e.target.value);
    setprecio_plan(0);
    setCuotas(3);
    setmonto_por_cuotas(0);
  };

  const handlePlanChange = (e) => {
    const planId = parseInt(e.target.value, 10);
    const plan = planes.find((p) => p.id === planId);
    setPlanSeleccionado(plan);
    setprecio_plan(0);
    setCuotas(3);
  };

  const handleCodigoChange = (e) => {
    setCodigo(e.target.value);
  };

  // Calcular montos
  useEffect(() => {
    if (!planSeleccionado || !modalidad_pago) {
      setprecio_plan(0);
      setmonto_por_cuotas(0);
      return;
    }

    if (modalidad_pago === "contado") {
      const precio = contadoChecked ? precioContado : precioContado_bcv;
      const metodo = contadoChecked ? "USD" : "VES";

      setmetodo_pago(metodo);
      setprecio_plan(precio);
      setmonto_por_cuotas(0);
    }

    // -------------------------------
    // Bloque principal ultra limpio
    if (modalidad_pago === "financiado") {
      const selectedQuotaOption = QUOTA_OPTIONS.find((q) => q.label === cuotas);

      // En este caso, el valor en cuota_X representa el PRECIO TOTAL del plan financiado a X meses
      const precioTotalFinanciado = selectedQuotaOption && planSeleccionado
        ? parseFloat(planSeleccionado[selectedQuotaOption.key]) || 0
        : 0;

      // La cuota mensual se calcula dividiendo ese total entre el número de meses
      const cuotaMensual = cuotas > 0 ? precioTotalFinanciado / cuotas : 0;

      setprecio_plan(precioTotalFinanciado);
      setmonto_por_cuotas(cuotaMensual);
    }
  }, [modalidad_pago, cuotas, planSeleccionado, contadoChecked]);

  // ===== Agregar al Contrato =====
  const handleAgregar = () => {
    if (!planSeleccionado || !modalidad_pago) {
      toast.error("Seleccione un plan y modalidad de pago.");
      return;
    }

    if (!codigo.trim()) {
      toast.error("Debe ingresar el código del nicho.");
      return;
    }

    const item = CrearItemServicio({
      codigo,
      tipo_servicio: "nicho-ataud",
      tipo_plan: "producto",
      descripcion: planSeleccionado.descripcion,
      modalidad_pago,
      numero_cuotas: modalidad_pago === "financiado" ? cuotas : null,
      monto_por_cuota: modalidad_pago === "financiado" ? monto_por_cuotas : null,
      precio_plan,

      detalles_adicionales: {
        codigo,
      },
    });

    agregarAlPresupuesto(item);
  };

  return (
    <>
      <Typography
        sx={{
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          textAlign: "center",
        }}
        variant="h5"
      >
        SOLICITUD DE CONTRATO DE NICHOS DE ATAUD
      </Typography>
      <Box>
        <Typography
          sx={{
            marginTop: "15px",
            marginBottom: "10px",
            fontFamily: "Bakbak One, sans-serif",
            color: "#0A4575",
          }}
          variant="h6"
        >
          PLAN A SOLICITAR
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <TextField
            fullWidth
            label="Código Nicho"
            variant="outlined"
            size="small"
            value={codigo}
            onChange={handleCodigoChange}
            sx={{ boxShadow: 3 }}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            sx={{ boxShadow: 3 }}
          >
            <InputLabel>Seleccione el Plan</InputLabel>
            <Select
              label="Seleccione el Plan"
              value={planSeleccionado ? planSeleccionado.id : ""}
              onChange={handlePlanChange}
              autoComplete="off"
            >
              <MenuItem value="" disabled>
                Seleccione
              </MenuItem>
              {planes
                .filter(
                  (plan) => plan.is_active === true || plan.is_active === 1
                )
                .map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.descripcion}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {planSeleccionado && (
          <Grid item xs={2}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              sx={{ boxShadow: 3 }}
            >
              <InputLabel id="modalidad_pago-label">Modalidad de Pago</InputLabel>
              <Select
                labelId="modalidad_pago-label"
                id="modalidad_pago"
                label="Modalidad de Pago"
                value={modalidad_pago}
                onChange={handlemodalidad_pagoChange}
                autoComplete="off"
              >
                <MenuItem value="" disabled>
                  Seleccione
                </MenuItem>
                <MenuItem value="contado">contado</MenuItem>
                <MenuItem value="financiado">financiado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {planSeleccionado && modalidad_pago && (
          <>
            {modalidad_pago === "contado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precio_plan.toFixed(2)}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contadoChecked}
                        onChange={(e) => setContadoChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Pago en Divisas"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}

            {modalidad_pago === "financiado" && (
              <>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    label="Precio del Plan"
                    variant="outlined"
                    size="small"
                    value={`$${precio_plan.toFixed(2)}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ boxShadow: 3 }}
                  >
                    <InputLabel id="cuotas-label">Cuotas</InputLabel>
                    <Select
                      labelId="cuotas-label"
                      id="cuotas"
                      label="Cuotas"
                      value={cuotas}
                      onChange={(e) => setCuotas(parseInt(e.target.value) || 3)}
                      autoComplete="off"
                    >
                      {availableQuotas.map((option) => (
                        <MenuItem key={option.label} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={1}>
                  <TextField
                    fullWidth
                    label="Monto por Cuota"
                    variant="outlined"
                    size="small"
                    value={`$${monto_por_cuotas.toFixed(2)}`}
                    sx={{ boxShadow: 3 }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAgregar}
                    disabled={deshabilitarBoton}
                    fullWidth
                  >
                    Crear Solicitud
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default NichosAtaud;
